<template>
  <div class="w-full">
    <a-form ref="formRef" :model="loginForm" :rules="rules" @finish="handleLogin" layout="vertical"
      class="w-full space-y-4">
      <!-- Email Field -->
      <a-form-item name="email" class="mb-4">
        <template #label>
          <span class="text-lg font-normal text-gray-900">{{ t('login.email') }}</span>
        </template>
        <a-input v-model:value="loginForm.email" :placeholder="t('login.enterEmail')" size="large" class="h-16"
          @keyup.enter="handleLogin">
          <template #prefix>
            <div class="flex items-center h-full px-2 border-r-2 border-gray-300">
              <MailOutlined class="text-lg" />
            </div>
          </template>
        </a-input>
      </a-form-item>

      <!-- Code Field with Send Button -->
      <a-form-item name="code" class="mb-4">
        <a-input v-model:value="loginForm.code" :placeholder="t('login.enterCode')" size="large" class="h-16"
          :maxlength="4" @keyup.enter="handleLogin">
          <template #prefix>
            <div class="flex items-center h-full px-2 border-r-2 border-gray-300">
              <LockOutlined class="text-lg" />
            </div>
          </template>
          <template #suffix>
            <a-button type="primary" :disabled="countdown > 0" @click.stop="handleSendCode"
              class="text-xs h-10 min-w-12 px-2">
              {{ countdown > 0 ? t('login.resendCode', { time: countdown }) : t('login.sendCode') }}
            </a-button>
          </template>
        </a-input>
      </a-form-item>

      <!-- Invitation Code Field -->
      <a-form-item name="invitationCode" class="mb-4">
        <template #label>
          <span class="text-lg font-normal text-gray-900">{{ t('login.invitationCode') }}</span>
        </template>
        <a-input v-model:value="loginForm.invitationCode" :placeholder="t('login.enterInvitationCode')" size="large"
          class="h-16" @keyup.enter="handleLogin">
          <template #prefix>
            <div class="flex items-center h-full px-2 border-r-2 border-gray-300">
              <LockOutlined class="text-lg" />
            </div>
          </template>
        </a-input>
      </a-form-item>

      <!-- Submit Button -->
      <a-form-item class="mb-0">
        <a-button type="primary" html-type="submit" size="large" :loading="loading" class="w-full h-16">
          {{ t('login.loginButton') }}
        </a-button>
      </a-form-item>
    </a-form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { message } from 'ant-design-vue'
import { MailOutlined, LockOutlined } from '@ant-design/icons-vue'
import { useI18n } from 'vue-i18n'
import { storageService } from '@/services/storageService'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { t, locale } = useI18n()
const formRef = ref()

// Form state
const loginForm = reactive({
  email: '',
  code: '',
  invitationCode: ''
})

const countdown = ref(0)
const loading = ref(false)
let countdownTimer: number | null = null

// Countdown localStorage key
const COUNTDOWN_KEY = 'login_code_countdown'
const COUNTDOWN_EMAIL_KEY = 'login_code_email'

// Form validation rules
const rules = computed(() => ({
  email: [
    { required: true, message: t('login.errors.emailRequired'), trigger: 'blur' as const },
    { type: 'email' as const, message: t('login.errors.invalidEmail'), trigger: 'blur' as const }
  ],
  code: [
    { required: true, message: t('login.errors.codeRequired'), trigger: 'blur' as const },
    {
      validator: (_rule: any, value: string) => {
        if (value && value.length !== 4) {
          return Promise.reject(t('login.errors.codeError'))
        }
        return Promise.resolve()
      },
      trigger: 'blur' as const
    }
  ]
}))

// Start countdown timer
const startCountdownTimer = (initialValue: number) => {
  countdown.value = initialValue

  if (countdownTimer) {
    clearInterval(countdownTimer)
  }

  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer!)
      countdownTimer = null
      // Clear localStorage when countdown completes
      localStorage.removeItem(COUNTDOWN_KEY)
      localStorage.removeItem(COUNTDOWN_EMAIL_KEY)
    }
  }, 1000)
}

// Handle external JWT authentication
onMounted(() => {
  const jwt = route.query.jwt as string
  const platformId = route.query.platform_id as string

  if (jwt && platformId) {
    handleExternalJWT(jwt, platformId)
  }

  // Check for invitation code in URL
  const invitationCode = route.query.invitation_code as string
  if (invitationCode) {
    loginForm.invitationCode = invitationCode
  }

  // Restore countdown from localStorage if exists
  const storedExpiration = localStorage.getItem(COUNTDOWN_KEY)
  const storedEmail = localStorage.getItem(COUNTDOWN_EMAIL_KEY)

  if (storedExpiration && storedEmail) {
    const expirationTime = parseInt(storedExpiration, 10)
    const now = Date.now()
    const remainingSeconds = Math.floor((expirationTime - now) / 1000)

    if (remainingSeconds > 0) {
      loginForm.email = storedEmail
      startCountdownTimer(remainingSeconds)
    } else {
      // Expired, clear storage
      localStorage.removeItem(COUNTDOWN_KEY)
      localStorage.removeItem(COUNTDOWN_EMAIL_KEY)
    }
  }
})

// Send verification code
const handleSendCode = async () => {
  if (!loginForm.email) {
    message.error(t('login.errors.emailRequired'))
    return
  }

  // Clear validation errors for the code field
  formRef.value?.clearValidate('code')

  try {
    const res: any = await authStore.sendAuthCode(loginForm.email)
    // Persist email for Profile page (Profile reads localStorage key `user_email`)
    const emailFromApi = res?.data?.email || loginForm.email
    storageService.saveAuthData({ email: emailFromApi })
    message.success(t('login.sendCode'))

    // Store expiration time in localStorage (60 seconds from now)
    const expirationTime = Date.now() + 60000
    localStorage.setItem(COUNTDOWN_KEY, expirationTime.toString())
    localStorage.setItem(COUNTDOWN_EMAIL_KEY, loginForm.email)

    // Start countdown
    startCountdownTimer(60)
  } catch (err: any) {
    message.error(err.message || 'Failed to send code')
  }
}

// Handle login
const handleLogin = async () => {
  if (!loginForm.email || !loginForm.code) {
    return
  }

  loading.value = true

  try {
    await authStore.loginWithEmailCode(
      loginForm.email,
      loginForm.code,
      loginForm.invitationCode || undefined
    )
    // Ensure email is persisted even if backend token response doesn't include it
    storageService.saveAuthData({ email: loginForm.email })

    // Clear countdown from localStorage on successful login
    localStorage.removeItem(COUNTDOWN_KEY)
    localStorage.removeItem(COUNTDOWN_EMAIL_KEY)
    if (countdownTimer) {
      clearInterval(countdownTimer)
    }

    // Store language preference in i18n format
    localStorage.setItem('locale', locale.value)

    // Redirect to phone page
    router.push('/phone')
  } catch (err: any) {
    message.error(t('login.errors.loginError'))
  } finally {
    loading.value = false
  }
}

// Handle external JWT login
const handleExternalJWT = async (jwt: string, platformId: string) => {
  loading.value = true

  try {
    await authStore.loginWithExternalJWT(jwt, platformId)

    // Store language preference in i18n format
    localStorage.setItem('locale', locale.value)

    router.push('/phone')
  } catch (err: any) {
    message.error(err.message || 'External JWT login failed')
  } finally {
    loading.value = false
  }
}
</script>
